import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, LoadingBlock, TextInput } from '../../components/ui.tsx';
import { createCredential, wasDismissed, webauthnSupported } from '../../lib/webauthn.ts';
import { partnerApi, partnerSecurityApi, PartnerApiError } from '../../api/partner.ts';
import { isFreshAuthRequired, usePartnerElevation } from '../../components/partner-elevation.tsx';
import { PARTNER_SESSION_QUERY_KEY, usePartnerIdentity } from '../../components/partner-layout.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';

/**
 * The partner's security screen.
 *
 * Two-factor authentication here is OPT-IN. `mfa_require_partner` ships off,
 * so on an ordinary deployment this page is an offer: a partner who never
 * opens it keeps the whole portal on their password alone, and one who enrols
 * a factor may remove it again. `status.required` is what a mandate looks
 * like, and only then does the wording become a demand.
 *
 * The single exception, and it is deliberate rather than a mandate in
 * disguise: changing the payout address needs a factor proved on the spot. See
 * `POST /account/payout-account` in the Worker for why that one cannot be
 * opted out of.
 */
export default function PartnerSecurityPage() {
  useDocumentTitle('Partner security');
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const elevation = usePartnerElevation();
  const identity = usePartnerIdentity();

  const [error, setError] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<{ methodId: string; secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const status = useQuery({
    queryKey: ['partner', 'security'],
    queryFn: partnerSecurityApi.status,
    retry: false,
  });

  /** Whatever changed here also changes what the shell believes. */
  function refreshAll() {
    void status.refetch();
    void queryClient.invalidateQueries({ queryKey: PARTNER_SESSION_QUERY_KEY });
  }

  /**
   * Ends this session and goes back to the sign-in screen, so the next one is
   * challenged on the factor this one never proved.
   *
   * Only on SUCCESS. Navigating from a `finally` meant a refused logout — a
   * dropped request, a rejected CSRF token — left the cookie alive and sent the
   * partner to a sign-in page that immediately recognized them, back into the
   * portal, and back here by the mandate: a button that visibly did nothing.
   */
  async function signInAgain() {
    try {
      await partnerApi.logout();
    } catch (caught) {
      setError(
        caught instanceof PartnerApiError
          ? `Could not sign you out: ${caught.message}`
          : 'Could not sign you out. Check your connection and try again.',
      );
      return;
    }
    window.location.assign('/partner/login');
  }

  const displayError = (caught: unknown, fallback: string, retry?: () => void) => {
    if (isFreshAuthRequired(caught)) {
      setError(null);
      elevation.begin(retry);
      return;
    }
    setError(caught instanceof PartnerApiError ? caught.message : fallback);
  };

  const startTotp = useMutation({
    mutationFn: partnerSecurityApi.enrollTotp,
    onSuccess: (result) => {
      setEnrollment(result);
      setError(null);
    },
    onError: (caught) => displayError(caught, 'Could not set up that authenticator.', () => startTotp.mutate()),
  });
  const verifyTotp = useMutation({
    mutationFn: () => partnerSecurityApi.verifyTotp(enrollment?.methodId ?? '', code),
    onSuccess: (result) => {
      setEnrollment(null);
      setCode('');
      if (result.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      refreshAll();
    },
    onError: (caught) => displayError(caught, 'That code is not right.'),
  });
  const addKey = useMutation({
    mutationFn: async () => {
      const options = await partnerSecurityApi.securityKeyOptions();
      const credential = await createCredential(options.publicKey);
      return credential
        ? partnerSecurityApi.registerSecurityKey({
            challengeId: options.challengeId,
            label: 'Security key',
            clientDataJson: credential.clientDataJson,
            attestationObject: credential.attestationObject,
            ...(credential.transports ? { transports: credential.transports } : {}),
          })
        : null;
    },
    onSuccess: (result) => {
      if (result?.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      refreshAll();
    },
    onError: (caught) => {
      if (!wasDismissed(caught)) displayError(caught, 'Could not register that security key.', () => addKey.mutate());
    },
  });
  const remove = useMutation({
    mutationFn: partnerSecurityApi.removeMethod,
    onSuccess: refreshAll,
    onError: (caught, id) => displayError(caught, 'Could not remove that method.', () => remove.mutate(id)),
  });
  const regenerate = useMutation({
    mutationFn: partnerSecurityApi.regenerateRecoveryCodes,
    onSuccess: (result) => {
      setRecoveryCodes(result.codes);
      refreshAll();
    },
    onError: (caught) => displayError(caught, 'Could not generate new recovery codes.', () => regenerate.mutate()),
  });

  if (status.isLoading) return <PortalReadState title="Security" label="Loading your security methods…" />;
  if (!status.data)
    return (
      <PortalReadState
        title="Security"
        error={status.error instanceof Error ? status.error.message : 'Security details could not be loaded.'}
        retry={() => status.refetch()}
      />
    );
  const data = status.data;
  // Read from the SHELL's state, not from "has no methods". The shell diverts
  // on `setupRequired`, which mirrors the Worker's gate — this session has not
  // proved a factor — and that is not the same as holding none: a partner who
  // enrolled in another browser, or whose deployment turned the mandate on
  // mid-session, arrives here already enrolled. Keying the banner off an empty
  // method list left exactly those people on a silent page whose every other
  // link redirected straight back to it.
  const mandated = identity.data?.mfa.setupRequired ?? (data.required && data.methods.length === 0);
  // `mfa.enrolled`, not `data.methods.length`. The status list holds every
  // active row; `enrolled` is filtered through `challengeMethodsFor`, the same
  // filter `decideLoginChallenge` applies. A partner whose only method is of a
  // type the policy has since turned off has rows but nothing a sign-in would
  // challenge on — telling them to sign in again would send them round the
  // same loop, when what they need is to enrol something still permitted.
  const enrolledButUnproven = mandated && (identity.data?.mfa.enrolled ?? false);

  return (
    <div className="portal-page">
      <WorkPage
        title="Security"
        description={
          data.required
            ? 'This site requires every partner to have a second sign-in code.'
            : 'A second sign-in code is optional. Adding one also lets you change your payout address.'
        }
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        {mandated ? (
          <div className="mb-4">
            <Alert tone="warning" title="A second sign-in code is required here">
              {enrolledButUnproven ? (
                <>
                  You already have a method set up, but this sign-in did not use it — so the rest of the portal stays
                  closed until it does.{' '}
                  <button
                    type="button"
                    className="text-[color:var(--color-brand)] underline"
                    onClick={() => void signInAgain()}
                  >
                    Sign in again
                  </button>{' '}
                  and you will be asked for it.
                </>
              ) : params.get('setup') === '1' ? (
                'Set one up now — the rest of the portal opens as soon as you have.'
              ) : (
                'Set one up to use the rest of the portal.'
              )}
            </Alert>
          </div>
        ) : null}

        {!data.enabled ? (
          <div className="mb-4">
            <Alert tone="info">A second sign-in code is turned off on this site.</Alert>
          </div>
        ) : null}

        {elevation.panel ? <div className="mb-4">{elevation.panel}</div> : null}

        {recoveryCodes ? (
          <section className="mb-4 rounded border border-amber-300 bg-amber-50 p-4">
            <h2 className="font-semibold">Save your recovery codes</h2>
            <p className="text-sm">They are shown once; each code works once.</p>
            <ul className="my-3 grid grid-cols-2 gap-1 font-mono text-sm">
              {recoveryCodes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(recoveryCodes.join('\n'))}>
              Copy codes
            </Button>{' '}
            <Button variant="ghost" onClick={() => setRecoveryCodes(null)}>
              Done
            </Button>
          </section>
        ) : null}

        <WorkSection title="Your methods">
          {data.methods.length ? (
            <ul className="mt-2 divide-y divide-[color:var(--color-line)]">
              {data.methods.map((method) => (
                <li className="flex items-center justify-between py-2" key={method.id}>
                  <span className="text-sm font-medium">{method.label}</span>
                  <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(method.id)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-[color:var(--color-muted)]">
              You have not set up a second factor. {data.required ? '' : 'You do not have to.'}
            </p>
          )}
          {enrollment ? (
            <div className="mt-4 space-y-3 rounded bg-[color:var(--color-surface)] p-3">
              <p className="text-sm">Add this account in your authenticator app, then enter its six-digit code.</p>
              <p className="break-all font-mono text-xs">{enrollment.uri}</p>
              <p className="text-sm">
                Setup key: <span className="font-mono">{enrollment.secret}</span>
              </p>
              <TextInput
                id="partner-totp-code"
                label="Code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              <Button variant="primary" disabled={verifyTotp.isPending} onClick={() => verifyTotp.mutate()}>
                Confirm
              </Button>{' '}
              <Button variant="ghost" onClick={() => setEnrollment(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {data.availableMethods.includes('totp') ? (
                <Button variant="primary" disabled={startTotp.isPending} onClick={() => startTotp.mutate()}>
                  Add an authenticator app
                </Button>
              ) : null}
              {data.availableMethods.includes('webauthn') && webauthnSupported() ? (
                <Button variant="secondary" disabled={addKey.isPending} onClick={() => addKey.mutate()}>
                  Add a security key
                </Button>
              ) : null}
            </div>
          )}
        </WorkSection>

        <WorkSection title="Recovery codes">
          <p className="text-sm text-[color:var(--color-muted)]">
            {data.recoveryCodesRemaining} unused {data.recoveryCodesRemaining === 1 ? 'code' : 'codes'} remaining.
          </p>
          <Button
            variant="secondary"
            className="mt-2"
            disabled={!data.methods.length || regenerate.isPending}
            onClick={() => regenerate.mutate()}
          >
            Generate new codes
          </Button>
        </WorkSection>
      </WorkPage>
    </div>
  );
}
